
import { PostgrestError, PostgrestSingleResponse } from '@supabase/supabase-js';
import { Json } from '@/integrations/supabase/types';
import { getFullName } from './name-helpers';

/**
 * Helper to safely check if a response has data
 */
export function hasData<T>(
  response: PostgrestSingleResponse<T>
): response is PostgrestSingleResponse<T> & { data: T } {
  return response.data !== null;
}

/**
 * Helper to check if a response has an error
 */
export function hasError(
  response: { error: PostgrestError | null }
): response is { error: PostgrestError } {
  return response.error !== null;
}

/**
 * Type guard to check if an object is a data object (not an error)
 */
export function isDataObject<T extends object>(obj: unknown): obj is T {
  return obj !== null && typeof obj === 'object' && !('error' in obj);
}

/**
 * Type-safe way to use "as const" for string literals used in queries
 */
export function asStringLiteral<T extends string>(value: T): T {
  return value;
}

/**
 * Debug helper for Supabase requests
 * Add this to any component where you're experiencing issues with Supabase requests
 */
export function debugSupabaseRequest(tableName: string, operation: string, error: any) {
  console.error(`Supabase ${operation} error for table ${tableName}:`, error);
  
  if (error?.message?.includes('406')) {
    console.warn('406 Not Acceptable error detected. This may be due to incorrect Accept headers.');
    console.info('Verify the Supabase client configuration has proper headers set.');
  }
  
  return error;
}

/**
 * Practitioner Profile interface
 * Use this for consistent typing when working with practitioner data
 */
export interface PractitionerProfile {
  id: string;
  first_name?: string;
  last_name?: string;
  professional_title?: string;
  bio?: string;
  languages_spoken?: string[];
  timezone?: string;
  years_of_experience?: number;
  accepting_new_clients: boolean;
  session_formats_offered?: string[];
  in_person_address?: string;
  default_video_tool?: string;
  preferred_language?: string;
  avatar_url?: string;
  profile_status?: string;
  admin_notes?: string | null;
  availability_rules?: Json | null;
  average_rating?: number | null;
  certifications?: Json | null;
  country?: string | null;
  notification_prefs?: Json | null;
  [key: string]: any;
}

/**
 * Export the getFullName function from name-helpers for convenience
 */
export { getFullName };
